<script lang="ts">
  import type { Companion, ExpenseParticipant } from "../../lib/types";
  import { expensesApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";

  let {
    expenseId,
    companions = [],
    currentParticipants = [],
    show = false,
    onClose,
    onSave,
  }: {
    expenseId: string;
    companions?: Companion[];
    currentParticipants?: ExpenseParticipant[];
    show?: boolean;
    onClose?: () => void;
    onSave?: (ids: string[]) => void;
  } = $props();

  let selectedIds: Set<string> = $state(new Set());
  let saving = $state(false);

  $effect(() => {
    if (show) {
      selectedIds = new Set(currentParticipants.map(p => p.id));
    }
  });

  function toggleParticipant(userId: string) {
    const next = new Set(selectedIds);
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    selectedIds = next;
  }

  function selectAll() {
    selectedIds = new Set(companions.map(c => c.user.id));
  }

  function selectNone() {
    selectedIds = new Set();
  }

  async function save() {
    if (selectedIds.size === 0) {
      addNotification("error", "최소 1명의 참여자가 필요합니다.");
      return;
    }

    saving = true;
    try {
      await expensesApi.updateParticipants(expenseId, Array.from(selectedIds));
      addNotification("success", "참여자가 업데이트되었습니다.");
      onSave?.(Array.from(selectedIds));
      onClose?.();
    } catch (error: any) {
      const message = error.response?.data?.errors?.[0]?.message || "참여자 업데이트에 실패했습니다.";
      addNotification("error", message);
    } finally {
      saving = false;
    }
  }

  function close() {
    onClose?.();
  }
</script>

{#if show}
  <!-- Backdrop -->
  <div 
    class="fixed inset-0 bg-black/50 z-40"
    onclick={close}
    onkeydown={(e) => e.key === "Escape" && close()}
    role="button"
    tabindex="0"
  ></div>

  <!-- Popup -->
  <div class="fixed z-50 bg-white rounded-xl shadow-2xl p-4 min-w-[280px] max-w-[320px]"
       style="top: 50%; left: 50%; transform: translate(-50%, -50%);">
    <div class="flex items-center justify-between mb-4">
      <h4 class="font-semibold text-gray-900">정산 참여자</h4>
      <button onclick={close} class="text-gray-400 hover:text-gray-600" aria-label="닫기">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Quick Actions -->
    <div class="flex gap-2 mb-3">
      <button
        onclick={selectAll}
        class="flex-1 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
      >
        전체 선택
      </button>
      <button
        onclick={selectNone}
        class="flex-1 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
      >
        전체 해제
      </button>
    </div>

    <!-- Participant List -->
    <div class="space-y-2 max-h-64 overflow-y-auto">
      {#each companions as companion (companion.id)}
        <label class="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={selectedIds.has(companion.user.id)}
            onchange={() => toggleParticipant(companion.user.id)}
            class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
            {#if companion.user.avatarUrl}
              <img src={companion.user.avatarUrl} alt={companion.user.name} class="w-full h-full rounded-full object-cover" />
            {:else}
              {companion.user.name.charAt(0)}
            {/if}
          </div>
          <span class="text-sm text-gray-900">{companion.user.name}</span>
        </label>
      {/each}
    </div>

    <!-- Selected Count -->
    <div class="mt-3 pt-3 border-t border-gray-100">
      <p class="text-xs text-gray-500 text-center mb-3">
        {selectedIds.size}명 선택됨 / {companions.length}명
      </p>
      
      <div class="flex gap-2">
        <button
          onclick={close}
          class="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
        >
          취소
        </button>
        <button
          onclick={save}
          disabled={saving || selectedIds.size === 0}
          class="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {saving ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  </div>
{/if}
